import { useState } from "react";
import { CHECKLIST_POINTS, sitesForSub, allSites } from "./checklistData";
import FileAttachments from "./FileAttachments";
import LinkedDocuments from "./LinkedDocuments";
import { PointPreview } from "./previews";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle2, Circle, ExternalLink, Info, Plus, Trash2, Globe, ChevronDown } from "lucide-react";

export default function ChecklistTab({ briefing, setBriefing, onGoTab, briefingId, aircraftId, pilotId, aircraft, nav }) {
  const checklist = briefing.checklist || {};
  const [active, setActive] = useState(1);
  const [newSub, setNewSub] = useState("");

  const setPoint = (id, key, value) =>
    setBriefing((b) => ({
      ...b,
      checklist: { ...b.checklist, [id]: { ...(b.checklist?.[id] || {}), [key]: value } },
    }));

  const point = CHECKLIST_POINTS.find((p) => p.id === active);
  const Icon = point.icon;
  const state = checklist[active] || {};
  const customSubs = state.customSubs || [];
  const sites = allSites(briefing);
  const subSiteIds = (subKey) => state.subSites?.[subKey] ?? sitesForSub(briefing, `${point.id}::${subKey}`).map((s) => s.id);
  const toggleSubSite = (subKey, id) => {
    const cur = subSiteIds(subKey);
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setPoint(active, "subSites", { ...(state.subSites || {}), [subKey]: next });
  };

  const subs = [
    ...(point.fields || []).map((f) => ({ key: f.key, label: f.label, type: f.type || "textarea", custom: false })),
    ...customSubs.map((c) => ({ key: c.key, label: c.label, type: "textarea", custom: true })),
  ];

  const addSub = () => {
    const label = newSub.trim();
    if (!label) return;
    setPoint(active, "customSubs", [...customSubs, { key: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label }]);
    setNewSub("");
  };
  const renameSub = (key, label) =>
    setPoint(active, "customSubs", customSubs.map((c) => (c.key === key ? { ...c, label } : c)));
  const removeSub = (key) =>
    setPoint(active, "customSubs", customSubs.filter((c) => c.key !== key));

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
      {/* Stepper */}
      <div className="glass h-fit rounded-2xl p-3">
        <div className="space-y-1">
          {CHECKLIST_POINTS.map((p) => {
            const done = checklist[p.id]?.done;
            const isActive = p.id === active;
            const PIcon = p.icon;
            return (
              <button key={p.id} data-testid={`checklist-step-${p.id}`} onClick={() => setActive(p.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all ${
                  isActive ? "bg-cyan-500/15 text-cyan-200" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"}`}>
                {done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> : <Circle className="h-4 w-4 shrink-0 text-slate-600" />}
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 font-mono-data text-[10px] text-slate-300">{p.id}</span>
                <span className="flex items-center gap-1.5 truncate"><PIcon className="h-3.5 w-3.5" /> {p.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail */}
      <div className="glass animate-fade-up rounded-2xl p-6" key={active}>
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/10">
              <Icon className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <div className="font-mono-data text-xs text-cyan-500/70">POINT {point.id} / 11</div>
              <h3 className="font-display text-xl font-bold text-slate-50">{point.title}</h3>
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
            <Checkbox checked={!!state.done} onCheckedChange={(v) => setPoint(point.id, "done", !!v)} data-testid={`checklist-done-${point.id}`} />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">Terminé</span>
          </label>
        </div>

        <div className="mb-5 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400">
            <Info className="h-3.5 w-3.5" /> À quoi ça sert
          </div>
          <p className="text-sm leading-relaxed text-slate-300">{point.purpose}</p>
          <ul className="mt-3 space-y-1.5">
            {point.checks.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-500" /> {c}
              </li>
            ))}
          </ul>
        </div>

        {[5, 6, 7, 8].includes(point.id) && (
          <div className="mb-5">
            <PointPreview pointId={point.id} briefing={briefing} aircraft={aircraft} nav={nav} briefingId={briefingId} />
          </div>
        )}

        {point.entity === "pilot" && (
          <div className="mb-5">
            <LinkedDocuments entityType="pilots" entityId={pilotId} title="Documents du pilote (fiche)"
              emptyLabel="Aucun pilote associé à ce briefing." manageHref={pilotId ? `/pilots/${pilotId}` : null} />
          </div>
        )}
        {point.entity === "aircraft" && (
          <div className="mb-5">
            <LinkedDocuments entityType="aircraft" entityId={aircraftId} title="Documents avion (fiche)"
              emptyLabel="Aucun avion associé à ce briefing." manageHref={aircraftId ? `/aircraft/${aircraftId}` : null} />
          </div>
        )}

        {/* Sub-items: each = comment + attached images */}
        <div className="space-y-4">
          {subs.map((sub) => (
            <div key={sub.key} data-testid={`subitem-${point.id}-${sub.key}`}
              className="rounded-xl border border-slate-800 bg-slate-950/30 p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                {sub.custom ? (
                  <Input value={sub.label} onChange={(e) => renameSub(sub.key, e.target.value)}
                    className="h-8 max-w-xs border-slate-700 bg-slate-900/60 text-sm font-semibold text-cyan-200"
                    data-testid={`subitem-label-${sub.key}`} />
                ) : (
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-cyan-400">{sub.label}</Label>
                )}
                {sub.custom && (
                  <button onClick={() => removeSub(sub.key)} data-testid={`remove-subitem-${sub.key}`}
                    className="rounded p-1.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              {sub.type === "textarea" ? (
                <Textarea value={state[sub.key] || ""} onChange={(e) => setPoint(point.id, sub.key, e.target.value)}
                  rows={2} placeholder="Commentaire / décision…" data-testid={`checklist-field-${point.id}-${sub.key}`}
                  className="mb-3 border-slate-700 bg-slate-900/60 text-slate-100" />
              ) : (
                <Input value={state[sub.key] || ""} onChange={(e) => setPoint(point.id, sub.key, e.target.value)}
                  placeholder="Commentaire…" data-testid={`checklist-field-${point.id}-${sub.key}`}
                  className="mb-3 border-slate-700 bg-slate-900/60 text-slate-100" />
              )}
              <FileAttachments briefingId={briefingId} pointId={`${point.id}::${sub.key}`} compact title="Images / documents joints" />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid={`sub-sites-trigger-${point.id}-${sub.key}`}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300">
                      <Globe className="h-3.5 w-3.5" /> Sites{subSiteIds(sub.key).length > 0 ? ` (${subSiteIds(sub.key).length})` : ""} <ChevronDown className="h-3 w-3" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 border-slate-700 bg-slate-900 p-2">
                    <div className="px-1 pb-1.5 text-[10px] uppercase tracking-wider text-slate-500">Sites de référence</div>
                    {sites.map((s) => (
                      <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 text-sm text-slate-200 hover:bg-slate-800">
                        <Checkbox checked={subSiteIds(sub.key).includes(s.id)} onCheckedChange={() => toggleSubSite(sub.key, s.id)}
                          data-testid={`sub-site-${point.id}-${sub.key}-${s.id}`} />
                        <span>{s.name}</span>
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
                <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                  {sites.filter((s) => subSiteIds(sub.key).includes(s.id)).map((s) => (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:underline">
                      {s.name} <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()}
              placeholder="Ajouter un sous-item (ex. Décision météo, Terrain X…)" data-testid={`add-subitem-input-${point.id}`}
              className="border-slate-700 bg-slate-900/60 text-slate-100" />
            <Button onClick={addSub} data-testid={`add-subitem-button-${point.id}`}
              className="shrink-0 bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
              <Plus className="mr-1 h-3.5 w-3.5" /> Sous-item
            </Button>
          </div>

          <FileAttachments briefingId={briefingId} pointId={`${point.id}`} title="Autres documents du point" />
        </div>
      </div>
    </div>
  );
}
